import { auditTime, catchError, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { formatDateOnlyToApi } from '@aston/foundation/functions';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { LanguageService } from '@aston/foundation';
import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';

import { SubsidiariesService } from '../../subsidiaries-module/services';
import * as subsidiariesActions from '../new-account-store/actions';
import { SubsidiaryStatus } from '../../subsidiaries-module/enums';
import { AppStoreActions, AppStoreSelectors } from '../app-store';
import * as Routes from '../../routes-definitions';

import * as featureActions from './actions';

@Injectable({providedIn: 'root'})
export class SubsidiariesStoreEffects {

	private subsidiariesService = inject(SubsidiariesService)
	private langs = inject(LanguageService)
	private actions$ = inject(Actions)
	private store = inject(Store)

	subsidiariesRequest$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.LoadSubsidiariesRequest, subsidiariesActions.SubsidiaryInitializedChanged),
		withLatestFrom(this.store.select(AppStoreSelectors.selectCurrentUserIsSupport)),
		auditTime(100),
		switchMap(([_, isSupport]) => (isSupport ? // When logged as support, fetch only active subsidiaries
			this.subsidiariesService.getActiveSubsidiaries() :
			this.subsidiariesService.getAllSubsidiaries()
		).pipe(
			map(allSubsidiaries => {
				const subsidiaries = allSubsidiaries.filter(s => s.status === SubsidiaryStatus.Initialized);
				const hasPendingItems = allSubsidiaries.some(s => s.status === SubsidiaryStatus.Registered);
				return featureActions.LoadSubsidiariesSuccess({entity: subsidiaries, hasPendingItems});
			}),
			catchError(error => of(featureActions.LoadSubsidiariesFailure({error})))
		))
	));

	redirectToTenant$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.RedirectToTenant),
		withLatestFrom(this.langs.language$),
		switchMap(([action, lang]) => this.subsidiariesService.getTenantUrl(action.subsidiaryId).pipe(
			map(tenantUrl => tenantUrl.withPath(action.path || Routes.MiddleWelcomeRoutePath).withParams({ lang })),
			tap(tenantUrl => window.location.href = tenantUrl.toString()),
			catchError(_ => of(AppStoreActions.ToastError('Errors.ErrorHasOccurred')))
		))
	), {dispatch: false});

	redirectToTenantNewTab$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.RedirectToTenantNewTab),
		withLatestFrom(this.langs.language$),
		switchMap(([action, lang]) => this.subsidiariesService.getTenantUrl(action.subsidiaryId).pipe(
			map(tenantUrl => tenantUrl.withPath(Routes.MiddleWelcomeRoutePath).withParams({ lang })),
			tap(tenantUrl => window.open(tenantUrl.toString(), '_blank')),
			catchError(_ => of(AppStoreActions.ToastError('Errors.ErrorHasOccurred')))
		))
	), {dispatch: false});

	confirmTenantTimeout$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.SetTenantTimeoutConfirm),
		map(action => AppStoreActions.OpenConfirmationModal(
			{textsKey: action.entity.date ? 'Subsidiaries.Confirmations.SetTenantTimeout.' : 'Subsidiaries.Confirmations.ClearTenantTimeout.'},
			featureActions.SetTenantTimeoutRequest(action.entity)
		))
	));

	updateTenantTimeout$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.SetTenantTimeoutRequest),
		switchMap(({entity}) => this.subsidiariesService.updateTenantTimeout(entity.id, entity.date ? formatDateOnlyToApi(entity.date) : null).pipe(
			switchMap(_ => [
				featureActions.SetTenantTimeoutSuccess({ entity }),
				featureActions.LoadSubsidiariesRequest(),
			]),
			catchError(error => of(featureActions.SetTenantTimeoutFailure({error})))
		))
	));
}
